import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackSelectDealFromDealTable } from '../../utils/usageTracker';
import { getDealSplitAmountForDealRow, hasDealSplit } from '../../utils/deals';
import SubmitForecastBulkActionButton from './SubmitForecastBulkActionButton';
import SelectedDealAmountTotal from './SelectedDealAmountTotal';
import { getDealsArray, getShowSubmissionPanelFromDealTable, getSelectedDealAmount, getTotalDealAmount } from '../../selectors/dealsSelectors';
import { getIsDealStageView } from '../../selectors/forecastSelectors';
import { getCompanyCurrency } from '../../selectors/multiCurrencySelectors';
import { getDealSplitsEnabled } from '../../selectors/portalSettings';
import { currencyToPreferredRequest } from '../../api/currencyApi';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { setSelectedDealAmount, setSelectedDealCount, setShowSubmissionPanelFromDealTable } from '../../actions/DealsActions';
import useFetchPreferredCurrency from '../multicurrency/hooks/useFetchPreferredCurrency';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// TODO: Remove this file when cleaning up Forecasting:MultipleOutcomes gate
const BulkActions = ({
  querySelected,
  selection
}) => {
  const dispatch = useDispatch();
  const deals = useSelector(getDealsArray);
  const isDealStageView = useSelector(getIsDealStageView);
  const totalDealAmount = useSelector(getTotalDealAmount);
  const companyCurrency = useSelector(getCompanyCurrency);
  const dealSplitsEnabled = useSelector(getDealSplitsEnabled);
  const isSubmissionPanelOpen = useSelector(getShowSubmissionPanelFromDealTable);
  const selectedDealAmountTotal = useSelector(getSelectedDealAmount);
  const {
    userHasDifferentPreferredCurrency
  } = useFetchPreferredCurrency();
  const selectedDeals = deals.filter(deal => selection.has(`${deal.dealId}`));
  useEffect(() => {
    // clean up selected deal count when bulk actions close
    return () => dispatch(setSelectedDealCount(0));
  }, [dispatch]);
  const handleClickSubmitForecastFromTable = useCallback(selectedDealAmount => {
    dispatch(setSelectedDealAmount(selectedDealAmount));
    dispatch(setSelectedDealCount(selectedDeals.length));
    if (!isSubmissionPanelOpen) {
      dispatch(setShowSubmissionPanelFromDealTable(true));
    }
  }, [dispatch, isSubmissionPanelOpen, selectedDeals.length]);
  const dealsWithDealSplitsSelected = !!selectedDeals.find(deal => hasDealSplit(deal, !!dealSplitsEnabled));
  const manuallySelectedDealsTotal = selectedDeals.reduce((acc, deal) => {
    let amountInHomeCurrency = deal.properties.amount_in_home_currency;
    // If property exists, check for deal split amount
    if (amountInHomeCurrency && amountInHomeCurrency.value) {
      amountInHomeCurrency = getDealSplitAmountForDealRow(deal, dealSplitsEnabled, amountInHomeCurrency.value);
    }
    return acc + Number(amountInHomeCurrency) || 0;
  }, 0);
  useEffect(() => {
    if (selectedDeals.length > 0) {
      trackSelectDealFromDealTable(isDealStageView);
    }
  }, [selectedDeals.length, isDealStageView]);
  const [totalLoading, setTotalLoading] = useState(false);
  const setTotalValue = useCallback(total => {
    const newTotalValue = {
      value: total.value,
      currencyCode: total.currencyCode || companyCurrency
    };
    dispatch(setSelectedDealAmount(newTotalValue));
    dispatch(setSelectedDealCount(selectedDeals.length));
  }, [companyCurrency, selectedDeals.length, dispatch]);
  const convertToPreferredCurrencyAndSetTotalValue = useCallback(total => {
    setTotalLoading(true);
    currencyToPreferredRequest({
      amount: total.value
    }).then(amountInPreferredCurrency => {
      setTotalValue({
        value: amountInPreferredCurrency.amount,
        currencyCode: amountInPreferredCurrency.currencyCode
      });
      setTotalLoading(false);
    }).catch(error => {
      setTotalLoading(false);
      logRavenException(error, 'Currency to Preferred Request', {
        total
      });
    });
  }, [setTotalValue]);
  useEffect(() => {
    // querySelected is a boolean that tells us if the user has clicked "Select all X records" in the table
    const total = querySelected ? totalDealAmount : {
      value: manuallySelectedDealsTotal,
      currencyCode: companyCurrency
    };
    if (total && typeof total.value === 'number') {
      if (userHasDifferentPreferredCurrency && !querySelected) {
        convertToPreferredCurrencyAndSetTotalValue(total);
      } else {
        setTotalValue(total);
      }
    }
  }, [companyCurrency, userHasDifferentPreferredCurrency, querySelected, totalDealAmount, manuallySelectedDealsTotal, isSubmissionPanelOpen, dispatch, convertToPreferredCurrencyAndSetTotalValue, handleClickSubmitForecastFromTable, setTotalValue]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    blockSize: "20px",
    children: [/*#__PURE__*/_jsx(SelectedDealAmountTotal, {
      dealsWithDealSplitsSelected: dealsWithDealSplitsSelected,
      totalToDisplay: selectedDealAmountTotal,
      totalLoading: totalLoading
    }), /*#__PURE__*/_jsx(SubmitForecastBulkActionButton, {
      isDealStageView: isDealStageView,
      onSubmitForecastFromTable: handleClickSubmitForecastFromTable
    })]
  });
};
export default BulkActions;